// src/pages/scheduler/kit.tsx — small shared primitives for the Scheduler app,
// ported from the prototype's theme.jsx / scheduler-list.jsx so the surfaces
// stay pixel-faithful. Uses the Hub's C/F tokens + the shared Icon set.

import type { CSSProperties, ReactNode } from "react";
import { C, F } from "../../theme";
import * as Icons from "../../components/ui/Icon";
import type { IconProps } from "../../components/ui/Icon";
import { STATUS } from "./types";

type IconComp = (p: IconProps) => JSX.Element;

export function iconByKey(key: string | undefined): IconComp {
  const entry = (Icons as unknown as Record<string, unknown>)[key ?? ""];
  return (typeof entry === "function" ? entry : Icons.Clock) as IconComp;
}

export function Eyebrow({ children, c = C.brassDk, style }: { children: ReactNode; c?: string; style?: CSSProperties }) {
  return (
    <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 13, fontWeight: 600, color: c, letterSpacing: 0.2, ...style }}>
      {children}
    </div>
  );
}

const PILL_TONES: Record<string, { bg: string; fg: string; bd: string }> = {
  brass:   { bg: C.brassBg, fg: C.brassDk, bd: "rgba(167,117,28,0.20)" },
  heather: { bg: C.heatherBg, fg: C.heatherDk, bd: "rgba(122,93,140,0.22)" },
  sage:    { bg: C.sageBg, fg: C.sageDk, bd: "rgba(110,140,114,0.22)" },
  signal:  { bg: C.signalBg, fg: C.signalDk, bd: "rgba(184,58,43,0.22)" },
  paper:   { bg: C.paperWarm, fg: C.inkSoft, bd: C.hairline },
};

export function Pill({ tone = "brass", children, small, style }: { tone?: string; children: ReactNode; small?: boolean; style?: CSSProperties }) {
  const t = PILL_TONES[tone] || PILL_TONES.brass;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: small ? "3px 9px" : "5px 11px", borderRadius: 999,
      background: t.bg, color: t.fg, fontSize: small ? 11 : 12, fontWeight: 800,
      letterSpacing: 0.4, textTransform: "uppercase", border: `1px solid ${t.bd}`,
      fontFamily: F.body, whiteSpace: "nowrap", ...style,
    }}>{children}</span>
  );
}

const STAMP_TONES: Record<string, { bg: string; fg: string; bd: string }> = {
  brass:   { bg: "#F6E1A7", fg: C.brassDk, bd: "#C9A458" },
  paper:   { bg: "#F2E5C2", fg: C.inkSoft, bd: C.hairline },
  heather: { bg: C.heatherBg, fg: C.heatherDk, bd: "#B89BC4" },
  sage:    { bg: C.sageBg, fg: C.sageDk, bd: "#9CB39E" },
};

export function Stamp({ children, tone = "brass", style }: { children: ReactNode; tone?: string; style?: CSSProperties }) {
  const t = STAMP_TONES[tone] || STAMP_TONES.brass;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", fontFamily: F.mono, fontSize: 11, fontWeight: 700,
      color: t.fg, background: t.bg, border: `1px dashed ${t.bd}`, borderRadius: 3, letterSpacing: 1, ...style,
    }}>{children}</span>
  );
}

export function StatusChip({ status, small }: { status: string; small?: boolean }) {
  const s = STATUS[status] || STATUS.skipped;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: small ? "3px 9px" : "4px 11px", borderRadius: 999,
      background: s.bg, border: `1px solid ${s.tone}22`,
      fontFamily: F.body, fontWeight: 800, fontSize: small ? 11 : 12, color: s.tone, letterSpacing: 0.3,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: s.dot, boxShadow: status === "running" ? `0 0 6px ${s.dot}` : "none" }} />
      {s.label}
    </span>
  );
}

export function Toggle({ on, onToggle, size = 1 }: { on: boolean; onToggle: () => void; size?: number }) {
  return (
    <button onClick={onToggle} role="switch" aria-checked={on} type="button" style={{
      width: 52 * size, height: 30 * size, borderRadius: 999,
      border: `1px solid ${on ? C.sageDk : C.hairline}`,
      background: on ? `linear-gradient(180deg, ${C.sage}, ${C.sageDk})` : C.paperWarm,
      position: "relative", cursor: "pointer", flexShrink: 0, transition: "all .18s",
      boxShadow: on ? "inset 0 1px 0 rgba(255,255,255,0.25)" : "inset 0 1px 2px rgba(122,84,11,0.08)",
    }}>
      <span style={{
        position: "absolute", top: 2 * size, left: on ? 24 * size : 2 * size, width: 24 * size, height: 24 * size,
        borderRadius: 999, background: C.card, boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left .18s",
      }} />
    </button>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "inline-flex", background: C.paperWarm, borderRadius: 12, padding: 3, border: `1px solid ${C.hairline}`, gap: 2 }}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
            padding: "8px 14px", borderRadius: 9, border: "none", cursor: "pointer",
            background: on ? C.card : "transparent", color: on ? C.ink : C.inkSoft,
            fontFamily: F.body, fontWeight: 700, fontSize: 13, letterSpacing: 0.2, textTransform: "capitalize",
            boxShadow: on ? C.shadowS : "none",
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}
